import type { OrganizationUserRole } from '@ent/permissions-shared';

/** Product policy for UI visibility. Ent privacy remains authoritative. */
export const CAPABILITIES = {
  ManageOrganization: 'organization.manage',
  ManageTeam: 'team.manage',
  ExportTeam: 'team.export',
  ManageUnavailableItems: 'unavailable-items.manage',
  ManageMenus: 'menus.manage',
  ManageRecipes: 'recipes.manage',
  ManageReference: 'reference.manage',
  CreateLogbookEntry: 'logbook.create',
  ManageChannels: 'channels.manage',
  SendMessages: 'messages.send',
  // Adding/removing/configuring agent identities in channels/DMs, and
  // reading `agent_profiles`. See docs/plans/2026-09-17-agentic-rag-and-conversations.md
  // Phase B/C. Not granted to hr/employee_lead/employee.
  ManageAgents: 'agents.manage'
} as const;

export type Capability = (typeof CAPABILITIES)[keyof typeof CAPABILITIES];

const ALL_CAPABILITIES = Object.values(CAPABILITIES);

export const ROLE_CAPABILITIES: Record<
  OrganizationUserRole,
  readonly Capability[]
> = {
  admin: ALL_CAPABILITIES,
  owner: ALL_CAPABILITIES,
  gm: ALL_CAPABILITIES,
  mgr: ALL_CAPABILITIES.filter(
    capability => capability !== CAPABILITIES.ManageOrganization
  ),
  hr: [
    CAPABILITIES.ManageTeam,
    CAPABILITIES.ManageUnavailableItems,
    CAPABILITIES.CreateLogbookEntry,
    CAPABILITIES.SendMessages
  ],
  employee_lead: [
    CAPABILITIES.ManageUnavailableItems,
    CAPABILITIES.SendMessages
  ],
  employee: [CAPABILITIES.ManageUnavailableItems, CAPABILITIES.SendMessages]
};

/** Roles from most to least privileged, for picking one row per user. */
export const ROLE_PRECEDENCE: readonly OrganizationUserRole[] = [
  'owner',
  'admin',
  'gm',
  'mgr',
  'hr',
  'employee_lead',
  'employee'
];

/** The most privileged of `roles`, or `undefined` when none is known. */
export function primaryRole(
  roles: readonly (OrganizationUserRole | null | undefined)[]
): OrganizationUserRole | undefined {
  let best: OrganizationUserRole | undefined;
  let bestRank = Number.POSITIVE_INFINITY;
  for (const role of roles) {
    if (!role) continue;
    const rank = ROLE_PRECEDENCE.indexOf(role);
    if (rank !== -1 && rank < bestRank) {
      best = role;
      bestRank = rank;
    }
  }
  return best;
}

export function hasCapability(
  role: OrganizationUserRole | null | undefined,
  capability: Capability,
  explicitGrants: readonly Capability[] = []
): boolean {
  if (explicitGrants.includes(capability)) return true;
  return role ? ROLE_CAPABILITIES[role].includes(capability) : false;
}
